import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon,
  FilterList as FilterListIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SysDateRangePicker from '../components/SysDateRangePicker';
import ChamadoAssuntoSelect from '../components/selects/ChamadoAssuntoSelect';
import ChamadosRankingListaDialog, { ChamadosRankingListaFilters } from '../components/modals/ChamadosRankingListaDialog';
import { ticketService, ChamadoRankingCliente } from '../services/ticketService';

const CORES_AVATAR = ['#1565c0', '#6a1b9a', '#2e7d32', '#c62828', '#e65100', '#0277bd', '#4a148c', '#1b5e20', '#880e4f', '#33691e'];

const MEDALHAS = [
  { cor: '#FFD700', bg: 'linear-gradient(135deg, #fff8e1 0%, #ffe082 100%)', icone: '🥇' },
  { cor: '#C0C0C0', bg: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)', icone: '🥈' },
  { cor: '#CD7F32', bg: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)', icone: '🥉' },
];

const dateToYmd = (date: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitials = (name: string) =>
  name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

const formatarTelefone = (numero?: string | null, ddi?: string | null): string => {
  if (!numero) return '-';
  let limpo = numero.replace(/\D/g, '');

  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }

  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }

  if (limpo.length === 10) {
    return `${ddiEncontrado ? `+${ddiEncontrado} ` : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  }
  if (limpo.length === 11) {
    return `${ddiEncontrado ? `+${ddiEncontrado} ` : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }

  return numero;
};

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
            {titulo}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>
            {valor}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const clickableLinkSx = {
  cursor: 'pointer',
  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
};

const clickableChipSx = {
  cursor: 'pointer',
  '&:hover': { opacity: 0.85, transform: 'scale(1.03)' },
};

const PodiumCard: React.FC<{
  posicao: number;
  cliente: ChamadoRankingCliente;
  onClienteClick: (cliente: ChamadoRankingCliente) => void;
  onChamadosClick: (cliente: ChamadoRankingCliente) => void;
}> = ({ posicao, cliente, onClienteClick, onChamadosClick }) => {
  const theme = useTheme();
  const medalha = MEDALHAS[posicao - 1];
  const corAvatar = CORES_AVATAR[cliente.id % CORES_AVATAR.length];
  const altura = posicao === 1 ? 108 : posicao === 2 ? 96 : 88;

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: posicao === 1 ? 0 : 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          minHeight: altura,
          p: 1.25,
          borderRadius: 2,
          textAlign: 'center',
          background: medalha.bg,
          border: `1.5px solid ${alpha(medalha.cor, 0.45)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: medalha.cor },
        }}
      >
        <Typography variant="body1" sx={{ mb: 0.25, lineHeight: 1 }}>{medalha.icone}</Typography>
        <Avatar sx={{ width: 38, height: 38, mx: 'auto', mb: 0.5, bgcolor: corAvatar, fontWeight: 700, fontSize: '0.8rem', border: `2px solid ${medalha.cor}` }}>
          {getInitials(cliente.nome_cliente)}
        </Avatar>
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          display="block"
          title={cliente.nome_cliente}
          onClick={() => onClienteClick(cliente)}
          sx={clickableLinkSx}
        >
          {cliente.nome_cliente}
        </Typography>
        <Chip
          label={`${cliente.chamados} chamado${cliente.chamados !== 1 ? 's' : ''}`}
          size="small"
          onClick={() => onChamadosClick(cliente)}
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: alpha(medalha.cor, 0.2),
            color: theme.palette.mode === 'dark' ? theme.palette.common.white : '#5d4037',
            ...clickableChipSx,
          }}
        />
      </Paper>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
        {posicao}º lugar
      </Typography>
    </Box>
  );
};

const RankingPosicao: React.FC<{ posicao: number }> = ({ posicao }) => {
  if (posicao <= 3) {
    const medalha = MEDALHAS[posicao - 1];
    return (
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: '0.75rem',
          bgcolor: alpha(medalha.cor, 0.2),
          color: medalha.cor,
          border: `1.5px solid ${alpha(medalha.cor, 0.5)}`,
        }}
      >
        {posicao}
      </Box>
    );
  }

  return (
    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ width: 26, textAlign: 'center' }}>
      {posicao}
    </Typography>
  );
};

const ChamadosRankingTab: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState<ChamadoRankingCliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterAssunto, setFilterAssunto] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [listaModal, setListaModal] = useState<{ title: string; subtitle?: string; filters: ChamadosRankingListaFilters } | null>(null);

  const fetchRanking = useCallback(async () => {
    setLoading(true);
    try {
      const dataInicio = dateToYmd(dateRange.startDate);
      const dataFim = dateToYmd(dateRange.endDate);
      const params: { data_inicio?: string; data_fim?: string; chamado_assunto_id?: number } = {};

      if (dataInicio && dataFim) {
        params.data_inicio = dataInicio;
        params.data_fim = dataFim;
      }
      if (filterAssunto) {
        params.chamado_assunto_id = filterAssunto;
      }

      const data = await ticketService.getRanking(Object.keys(params).length ? params : undefined);
      setClientes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erro ao carregar ranking de chamados');
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, filterAssunto]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  useEffect(() => {
    setPage(0);
  }, [dateRange.startDate, dateRange.endDate, filterAssunto]);

  const clientesPaginados = useMemo(
    () => clientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [clientes, page, rowsPerPage]
  );

  const resumo = useMemo(() => {
    const totalChamados = clientes.reduce((acc, c) => acc + c.chamados, 0);
    const top = clientes[0];
    const media = clientes.length ? (totalChamados / clientes.length).toFixed(1) : '0';
    return { totalChamados, top, media };
  }, [clientes]);

  const topTres = useMemo(() => clientes.slice(0, 3), [clientes]);
  const maxChamados = useMemo(() => Math.max(...clientes.map((c) => c.chamados), 1), [clientes]);

  const buildListaFilters = (): ChamadosRankingListaFilters => {
    const filters: ChamadosRankingListaFilters = {};
    const dataInicio = dateToYmd(dateRange.startDate);
    const dataFim = dateToYmd(dateRange.endDate);
    if (dataInicio && dataFim) {
      filters.data_inicio = dataInicio;
      filters.data_fim = dataFim;
    }
    if (filterAssunto) {
      filters.chamado_assunto_id = filterAssunto;
    }
    return filters;
  };

  const abrirPaginaCliente = (clienteId: number) => {
    navigate(`/clients/${clienteId}`);
  };

  const abrirListaChamados = (cliente: ChamadoRankingCliente) => {
    setListaModal({
      title: `Chamados — ${cliente.nome_cliente}`,
      subtitle: `${cliente.chamados} chamado${cliente.chamados !== 1 ? 's' : ''} no período`,
      filters: { ...buildListaFilters(), cliente_id: cliente.id },
    });
  };

  return (
    <Box>
      {!loading && clientes.length > 0 && (
        <Grid container spacing={1} mb={1}>
          <Grid item xs={6} md={4}>
            <KpiCard
              titulo="Clientes no Ranking"
              valor={clientes.length}
              icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <KpiCard
              titulo="Total de Chamados"
              valor={resumo.totalChamados}
              icone={<AssignmentIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <KpiCard
              titulo="Média por Cliente"
              valor={resumo.media}
              subtitulo={resumo.top ? `Top: ${resumo.top.chamados}` : undefined}
              icone={<TrendingUpIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          </Grid>
        </Grid>
      )}

      {!loading && topTres.length > 0 && (
        <Paper elevation={0} sx={{ mb: 1, p: { xs: 1.25, md: 1.5 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
            <WorkspacePremiumIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
            <Typography variant="body2" fontWeight={700}>Top 3 Clientes</Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
            {topTres.length >= 2 && (
              <PodiumCard
                posicao={2}
                cliente={topTres[1]}
                onClienteClick={(c) => abrirPaginaCliente(c.id)}
                onChamadosClick={abrirListaChamados}
              />
            )}
            {topTres.length >= 1 && (
              <PodiumCard
                posicao={1}
                cliente={topTres[0]}
                onClienteClick={(c) => abrirPaginaCliente(c.id)}
                onChamadosClick={abrirListaChamados}
              />
            )}
            {topTres.length >= 3 && (
              <PodiumCard
                posicao={3}
                cliente={topTres[2]}
                onClienteClick={(c) => abrirPaginaCliente(c.id)}
                onChamadosClick={abrirListaChamados}
              />
            )}
          </Stack>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={6}>
            <SysDateRangePicker label="Data Chamado" value={dateRange} onChange={(range) => setDateRange(range)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChamadoAssuntoSelect
              value={filterAssunto}
              onChange={setFilterAssunto}
              label="Assunto"
              placeholder="Todos os assuntos"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" fontWeight={700}>Listagem Completa</Typography>
          {!loading && (
            <Chip
              size="small"
              label={`${clientes.length} cliente${clientes.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando ranking...</Typography>
          </Box>
        ) : clientes.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhum cliente encontrado</Typography>
            <Typography variant="caption" color="text.secondary">Ajuste os filtros ou aguarde novos chamados.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell width={44}>#</TableCell>
                    <TableCell width={56}>Id</TableCell>
                    <TableCell>Nome Cliente</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Chamados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesPaginados.map((cliente, index) => {
                    const posicaoGlobal = page * rowsPerPage + index + 1;
                    const corAvatar = CORES_AVATAR[cliente.id % CORES_AVATAR.length];
                    const intensidade = cliente.chamados / maxChamados;

                    return (
                      <TableRow
                        key={cliente.id}
                        hover
                        sx={{
                          '& .MuiTableCell-root': { py: 0.6, px: 1 },
                          ...(posicaoGlobal <= 3 ? { bgcolor: alpha(MEDALHAS[posicaoGlobal - 1].cor, 0.04) } : {}),
                        }}
                      >
                        <TableCell><RankingPosicao posicao={posicaoGlobal} /></TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>{cliente.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: corAvatar, fontSize: '0.65rem', fontWeight: 700 }}>
                              {getInitials(cliente.nome_cliente)}
                            </Avatar>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              noWrap
                              onClick={() => abrirPaginaCliente(cliente.id)}
                              sx={clickableLinkSx}
                            >
                              {cliente.nome_cliente}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <PhoneIcon sx={{ fontSize: 13, color: 'success.main', opacity: 0.8 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatarTelefone(cliente.numero, cliente.ddi)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={cliente.chamados}
                            size="small"
                            onClick={() => abrirListaChamados(cliente)}
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              minWidth: 36,
                              bgcolor: alpha(theme.palette.primary.main, 0.08 + intensidade * 0.25),
                              color: intensidade > 0.6 ? theme.palette.primary.dark : theme.palette.primary.main,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2 + intensidade * 0.3)}`,
                              ...clickableChipSx,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              size="small"
              count={clientes.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas:"
              sx={{
                minHeight: 44,
                '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 },
              }}
            />
          </>
        )}
      </Paper>

      <ChamadosRankingListaDialog
        open={Boolean(listaModal)}
        onClose={() => setListaModal(null)}
        title={listaModal?.title || ''}
        subtitle={listaModal?.subtitle}
        filters={listaModal?.filters || {}}
      />
    </Box>
  );
};

export default ChamadosRankingTab;
